import type { Theme, SxProps } from '@mui/material/styles';

import { useLocation } from 'react-router';
import { useMemo, useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';

import { useRouter } from 'src/routes/hooks';

import { ENVS, getEnv } from 'src/config/envs';

import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';
import { RenderLabel } from 'src/components/datagrid/DataGridCommon';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

type Product = {
  id: string;
  name: string;
  url: string;
  icon: string;
  disabled?: boolean;
};

const products: Product[] = [
  {
    id: 'dashboard',
    name: 'App Administration',
    url: '/dashboard',
    icon: 'solar:cpu-bold-duotone',
  },
  {
    id: 'insights',
    name: 'Insights',
    url: '/insights',
    icon: 'solar:graph-up-bold-duotone',
  },
];

export function ProductPicker() {
  const mediaQuery = 'sm';

  const { user } = useAuthContext();

  const { open, anchorEl, onClose, onOpen } = usePopover();

  const location = useLocation();
  const router = useRouter();

  const basePath = location.pathname.split('/')[1];

  const productsWithAccess = useMemo(
    () =>
      products.filter((product) => {
        if (product.id === 'dashboard') {
          return user?.role === 'super_admin';
        }
        return true;
      }),
    [user]
  );

  const currentProduct = productsWithAccess.find((option) => option.id === basePath);

  const currentEnv = getEnv();

  const handleChangeWorkspace = useCallback(
    (newValue: Product) => {
      router.push(newValue.url);
      onClose();
    },
    [onClose, router]
  );

  const buttonBg: SxProps<Theme> = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  if (!currentProduct) {
    return null;
  }

  const renderButton = () => (
    <>
      <ButtonBase
        disableRipple
        onClick={onOpen}
        sx={[
          {
            py: 0.5,
            gap: { xs: 0.5, [mediaQuery]: 1 },
            '&::before': buttonBg,
          },
        ]}
      >
        <Iconify
          icon={currentProduct?.icon}
          width={20}
          height={20}
          sx={{ color: 'text.secondary' }}
        />

        <Box
          component="span"
          sx={{ typography: 'subtitle2', display: { xs: 'none', [mediaQuery]: 'inline-flex' } }}
        >
          {currentProduct?.name}
        </Box>

        <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
      </ButtonBase>
      {currentEnv === ENVS.PRODUCTION && (
        // FIXME: mobile support
        <RenderLabel
          color="warning"
          sx={{
            ml: 2,
          }}
        >
          Warning: this is the production environment
        </RenderLabel>
      )}
    </>
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        arrow: { placement: 'top-left' },
        paper: { sx: { mt: 0.5, ml: -1.55 } },
      }}
    >
      <MenuList sx={{ width: 240 }}>
        {productsWithAccess.map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === currentProduct?.id}
            onClick={() => handleChangeWorkspace(option)}
            sx={{ height: 48 }}
            disabled={option.disabled}
          >
            <Iconify icon={option.icon} width={20} height={20} sx={{ color: 'text.secondary' }} />
            <Box component="span" sx={{ flexGrow: 1, fontWeight: 'fontWeightMedium' }}>
              {option.name}
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      {renderButton()}
      {renderMenuList()}
    </>
  );
}
